import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { useDialog } from "../../lib/dialog";
import { useSettings } from "../../lib/settings";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import { Card, Loader } from "./Dashboard";
import { Plus, Trash2, Pencil, X, Gift } from "lucide-react";

const EMPTY = { name: "", date: "", start_time: "19:00", end_time: "23:00", pay_rate_type: "performance_fee", pay_rate_amount: 60 };

export default function ShowDates() {
  const dialog = useDialog();
  const { formatFull } = useSettings();
  const { isArchived } = useEvents();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  // Update 27 Change 05: bonus pay modal
  const [bonusFor, setBonusFor] = useState(null);
  const [bonusAmount, setBonusAmount] = useState("");
  const [bonusNote, setBonusNote] = useState("");

  const load = () => adminApi.get("/admin/show-dates").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  const openBonus = (s) => { setBonusFor(s); setBonusAmount(s.bonus_amount ? String(s.bonus_amount) : ""); setBonusNote(s.bonus_note || ""); };
  const saveBonus = async (amount) => {
    const amt = amount !== undefined ? amount : (parseFloat(bonusAmount) || 0);
    await adminApi.post(`/admin/show-dates/${bonusFor.id}/bonus`, { amount: amt, note: amt > 0 ? bonusNote : "" });
    setBonusFor(null); load();
  };

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name || "", date: (s.date || "").slice(0, 10), start_time: s.start_time || "19:00", end_time: s.end_time || "23:00", pay_rate_type: s.pay_rate_type || "performance_fee", pay_rate_amount: s.pay_rate_amount ?? 60 });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name || !form.date) return;
    if (editingId) await adminApi.patch(`/admin/show-dates/${editingId}`, form);
    else await adminApi.post("/admin/show-dates", form);
    closeForm(); load();
  };
  const del = async (id) => {
    if (await dialog.confirm({ title: "Delete this open date?", description: "Removing an open date will also remove it from any actor's selected availability.", confirmLabel: "Delete", destructive: true })) {
      await adminApi.delete(`/admin/show-dates/${id}`); load();
    }
  };

  return (
    <AdminLayout title="Open Dates" subtitle="Set up the season — applicants will see and pick from these dates"
      actions={!isArchived && <button onClick={() => (showForm ? closeForm() : openCreate())} data-testid="new-show-date-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]"><Plus size={14} /> New Open Date</button>}>
      {showForm && (
        <Card title={editingId ? "Edit Open Date" : "Add Open Date"} className="mb-4">
          <div className="grid md:grid-cols-3 gap-3">
            <I label="Name (e.g. Opening Night)" value={form.name} onChange={(v) => setForm({...form, name: v})} testid="sd-name" />
            <I label="Date" type="date" value={form.date} onChange={(v) => setForm({...form, date: v})} testid="sd-date" />
            <div></div>
            <I label="Start" type="time" value={form.start_time} onChange={(v) => setForm({...form, start_time: v})} />
            <I label="End" type="time" value={form.end_time} onChange={(v) => setForm({...form, end_time: v})} />
            <div>
              <label className="label-mini block mb-1.5">Pay Type</label>
              <select value={form.pay_rate_type} onChange={(e) => setForm({...form, pay_rate_type: e.target.value})} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm">
                <option value="performance_fee">Performance Fee (per night)</option>
                <option value="hourly">Hourly Rate</option>
              </select>
            </div>
            <I label={`Rate (£) ${form.pay_rate_type === "hourly" ? "per hour" : "per night"}`} type="number" value={form.pay_rate_amount} onChange={(v) => setForm({...form, pay_rate_amount: parseFloat(v) || 0})} />
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} data-testid="sd-save-btn" className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">{editingId ? "Save Changes" : "Save"}</button>
            <button onClick={closeForm} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#3a3835] flex items-center gap-1.5"><X size={13} /> Cancel</button>
          </div>
        </Card>
      )}

      {!items ? <Loader /> : (
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#1a1a1a] text-left">
              <th className="px-4 py-3 label-mini">Date</th><th className="px-4 py-3 label-mini">Name</th>
              <th className="px-4 py-3 label-mini">Time</th><th className="px-4 py-3 label-mini">Pay</th>
              <th className="px-4 py-3 label-mini text-right">Actions</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-[#6B6862]">No open dates yet — add your first season date above.</td></tr>}
              {items.map((s) => (
                <tr key={s.id} className="border-b border-[#1a1a1a]">
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{formatFull(s.date)}</td>
                  <td className="px-4 py-3">{s.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#A19D94]">{s.start_time}–{s.end_time}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    £{s.pay_rate_amount} <span className="text-[#6B6862]">/{s.pay_rate_type === "hourly" ? "hr" : "night"}</span>
                    {s.bonus_amount > 0 && <div className="mt-1 text-[10px] text-[#3B82F6] flex items-center gap-1" data-testid={`sd-bonus-chip-${s.id}`}><Gift size={10} /> +£{s.bonus_amount} bonus{s.bonus_note ? ` · ${s.bonus_note}` : ""}</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {!isArchived && <>
                      <button onClick={() => openBonus(s)} data-testid={`sd-bonus-${s.id}`} className="text-[#6B6862] hover:text-[#3B82F6] mr-3" title="Bonus pay"><Gift size={14} /></button>
                      <button onClick={() => openEdit(s)} data-testid={`sd-edit-${s.id}`} className="text-[#6B6862] hover:text-[#F2EBE1] mr-3" title="Edit"><Pencil size={14} /></button>
                      <button onClick={() => del(s.id)} data-testid={`sd-delete-${s.id}`} className="text-[#6B6862] hover:text-blood" title="Delete"><Trash2 size={14} /></button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bonusFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setBonusFor(null)} data-testid="sd-bonus-modal">
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[#1a1a1a] flex items-center justify-between">
              <div>
                <h3 className="font-display text-xl uppercase tracking-tight">Bonus Pay</h3>
                <p className="text-xs text-[#6B6862] mt-1">{bonusFor.name} · {formatFull(bonusFor.date)}</p>
              </div>
              <button onClick={() => setBonusFor(null)} className="text-[#6B6862] hover:text-blood"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-[#A19D94]">A one-off amount added on top of the standard rate for <strong>every actor who signs in</strong> on this night. Shown as a separate line in payroll.</p>
              <I label="Bonus Amount (£)" type="number" value={bonusAmount} onChange={setBonusAmount} testid="sd-bonus-amount" />
              <I label="Note (optional, e.g. Bank holiday uplift)" value={bonusNote} onChange={setBonusNote} testid="sd-bonus-note" />
            </div>
            <div className="p-5 border-t border-[#1a1a1a] flex justify-between gap-2">
              {bonusFor.bonus_amount > 0 ? (
                <button onClick={() => saveBonus(0)} data-testid="sd-bonus-remove" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood">Remove Bonus</button>
              ) : <span />}
              <div className="flex gap-2">
                <button onClick={() => setBonusFor(null)} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#3a3835]">Cancel</button>
                <button onClick={() => saveBonus()} data-testid="sd-bonus-save" className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">Save Bonus</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const I = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="label-mini block mb-1.5">{label}</label>
    <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
  </div>
);

